import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {forkJoin, Observable, of, Subscription} from 'rxjs';
import {SearchService} from '../../../../core/services/search.service';
import {TilesService} from '../../../../core/services/tiles.service';
import {AppState} from '../../../../app/app.state';
import {Store} from '@ngrx/store';
import {AlarmEvent, LORAWAN_GATEWAY_INACTIVE} from '../../../../shared/models/alarm';
import {Tile} from '../../../../shared/models/tile';
import {Hub} from '../../../../shared/models/hub';
import {Device} from '../../../../shared/models/device';
import {TitleBreadcrumbsChangedAction, TitleChangedAction} from '../../../../core/actions/title.actions';
import {SortTable, SortTableRow} from '../../../../shared/models/sort-table/types';
import {
  SortTableCellButtons, SortTableCellEmpty,
  SortTableCellIcon,
  SortTableCellKebabMenu,
  SortTableCellStatusDot,
  SortTableCellTextPill,
  SortTableStatusDotColor,
} from '../../../../shared/models/sort-table/cell-classes';
import {AlarmsService} from '../../../../core/services/alarms.service';
import {Option} from '../../../../shared/models/option';
import {UrlService} from '../../../../core/services/url.service';
import {UtilsService} from '../../../../core/services/utils.service';
import {BaseTableViewComponent} from '../../../components/_base-table-view/base-table-view.component';
import {makeObjectKey, ObjectKey, ObjectModel, ObjectModelKeyMap, ObjectType} from '../../../../shared/models/object.model';
import {HubsService} from '../../../../core/services/hubs.service';
import {DevicesService} from '../../../../core/services/devices.service';
import {MotiChatService} from '@moti/services/moti-chat.service';
import {PermissionsService} from '../../../../core/services/permissions.service';
import {Permission} from '../../../../shared/models/fga/permission';
import {SessionService} from '../../../../core/services/session.service';
import {PillLetter} from '../../../../shared/models/pill';
import {TileIcon} from '../../../../shared/models/tile-icon';
import {SortTableComponent} from '../../../components/sort-table/sort-table.component';
import {SpinnerComponent} from '../../../components/spinner/spinner.component';
import {StatStringComponent} from '../../../components/stat-string/stat-string.component';

const COL_IDX_KEBAB = 0;
const COL_IDX_SUBJECT = 1;
const COL_IDX_ICON = 2;
const COL_IDX_STATUS = 3;
const COL_IDX_BUTTON = 4;
const COL_IDX_START_TIME = 5;


@Component({
  selector: 'motn-alarms-dashboard',
  templateUrl: './alarms-dashboard.component.html',
  styleUrls: ['./alarms-dashboard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SortTableComponent,
    SpinnerComponent,
    StatStringComponent,
  ],
})
export class AlarmsDashboardComponent extends BaseTableViewComponent implements OnInit, OnDestroy {
  // The keys in each of the following two are the same.
  // Regular alarms use ObjectKey (type.id); LoRaWAN gateway alarms use alarm UUID
  // because a single hub can have multiple active gateway alarms simultaneously.
  private activeAlarms: { [alarmKey: string]: AlarmEvent } = {};
  private activeSubjects: ObjectModelKeyMap = {};

  private searchTerm: string = '';

  loading: boolean = true;
  private alarmsLoaded: boolean = false;

  totalCount: number = 0;
  ackedCount: number = 0;
  unackedCount: number = 0;

  private alarmsSub: Subscription;
  private searchSub: Subscription;


  constructor(
    private store: Store<AppState>,
    private alarmsService: AlarmsService,
    private tilesService: TilesService,
    private hubsService: HubsService,
    private devicesService: DevicesService,
    private searchService: SearchService,
    private urlService: UrlService,
    private motiChatService: MotiChatService,
    private permissionsService: PermissionsService,
    private session: SessionService,
  ) {
    super();
  }


  ngOnInit(): void {
    this.searchService.enableSearch();
    this.updateTitle();

    this.alarmsSub = this.store.select('alarms').subscribe(
      (state) => {
        this.alarmsLoaded = state.loaded;
        this.totalCount = state.activeEvents.length;
        this.ackedCount = state.activeEvents.filter(e => !!e.AckTime).length;
        this.unackedCount = this.totalCount - this.ackedCount;

        const activeAlarms: { [alarmKey: string]: AlarmEvent } = {};

        let getTiles$: Observable<Tile[]>;
        let getHubs$: Observable<Hub[]>;
        let getDevices$: Observable<Device[]>;

        for (let alarm of state.activeEvents) {
          const alarmKey = this.makeAlarmKey(alarm);

          activeAlarms[alarmKey] = alarm;

          switch (alarm.ObjectType) {
            case ObjectType.TILE: {
              getTiles$ = getTiles$ || this.tilesService.getUserTiles$();
              break;
            }
            case ObjectType.HUB: {
              getHubs$ = getHubs$ || this.hubsService.getUserHubs$();
              break;
            }
            case ObjectType.DEVICE: {
              getDevices$ = getDevices$ || this.devicesService.getUserDevices$();
              break;
            }
          }
        }

        const gets$: Observable<any>[] = [
          getTiles$,
          getHubs$,
          getDevices$,
        ].filter(g$ => g$);

        (gets$.length ? forkJoin(gets$) : of(null)).subscribe({
          next: () => {
            this.activeAlarms = activeAlarms;
            this.render();
          },
        });
      },
    );

    this.searchSub = this.urlService.searchTerm$.subscribe(
      (val) => {
        this.searchTerm = val;
        this.render();
      },
    );
  }


  ngOnDestroy() {
    this.alarmsSub?.unsubscribe();
    this.searchSub?.unsubscribe();
  }


  private makeAlarmKey(alarm: AlarmEvent): string {
    return alarm.IdempotencyKey ? alarm.UUID : makeObjectKey(alarm.ObjectType, alarm.ObjectID);
  }


  private render() {
    for (let alarmKey in this.activeAlarms) {
      const alarm = this.activeAlarms[alarmKey];
      const subj = new ObjectModel({ type: alarm.ObjectType as ObjectType, id: alarm.ObjectID });

      switch (alarm.ObjectType) {
        case ObjectType.TILE: {
          const tile = this.tilesService.sessUserTiles[alarm.ObjectID];

          if (!tile) {
            continue;
          }

          subj.name = tile.Name;

          break;
        }
        case ObjectType.HUB: {
          const hub = this.hubsService.sessUserHubs[alarm.ObjectID];

          if (!hub) {
            continue;
          }

          subj.name = hub.Name;

          break;
        }
        case ObjectType.DEVICE: {
          const device = this.devicesService.sessUserDevices[alarm.ObjectID];

          if (!device) {
            continue;
          }

          subj.name = device.Name;

          break;
        }
      }

      if (this.searchTerm) {
        if (subj.name.toLowerCase().includes(this.searchTerm.toLowerCase())) {
          this.activeSubjects[alarmKey] = subj;
        } else if (alarm.UUID.includes(this.searchTerm)) {
          this.activeSubjects[alarmKey] = subj;
        } else if (this.searchTerm === subj.id) {
          this.activeSubjects[alarmKey] = subj;
        } else {
          delete this.activeSubjects[alarmKey];
        }
      } else {
        this.activeSubjects[alarmKey] = subj;
      }
    }

    // Clear out tiles that are no longer appearing in the store's current
    // events array
    const activeSubjects: ObjectModelKeyMap = {};

    for (let subjKey in this.activeSubjects) {
      const alarmEvent = this.activeAlarms[subjKey];

      if (alarmEvent) {
        activeSubjects[subjKey] = this.activeSubjects[subjKey];
      }
    }

    this.activeSubjects = activeSubjects;

    this.updateTable();
  }


  protected updateTable() {
    const colKebabWidth = 40;
    const colIconWidth = 40;
    const colNameWidth = 200;
    const colStatusWidth = 150;
    const colStartTimeWidth = 260;
    const colButtonWidth = 130;

    const table: SortTable = {
      css: {
        'min-width': (colIconWidth + colNameWidth + colStatusWidth + colStartTimeWidth + colButtonWidth + colKebabWidth) + 'px',
      },
      columns: [],
      rows: [],
      pagination: {
        enabled: true,
        rowsPerPage: 25,
        startOnPage: 1,
        maxPageOptions: 12,
      },
    };

    {
      table.columns[COL_IDX_KEBAB] = {
        name: '',
        css: {
          'width': colKebabWidth + 'px',
          'justify-content': 'center',
        },
      };

      table.columns[COL_IDX_ICON] = {
        name: '',
        css: {
          'justify-content': 'center',
          'width': colIconWidth + 'px',
        },
      };

      table.columns[COL_IDX_SUBJECT] = {
        name: 'Subject',
        css: {
          'width': `calc(100% - ${colIconWidth + colStatusWidth + colStartTimeWidth + colButtonWidth + colKebabWidth}px)`,
          'font-weight': '600',
        },
        sortingFn: SortTableCellTextPill.sortingFnMainText,
      };

      table.columns[COL_IDX_STATUS] = {
        name: 'Status',
        css: {
          'width': colStatusWidth + 'px',
          'justify-content': 'center',
        },
        sortingFn: SortTableCellStatusDot.sortingFnCustomSortValue,
      };

      table.columns[COL_IDX_START_TIME] = {
        name: 'Start Time',
        css: {
          'width': colStartTimeWidth + 'px',
          'justify-content': 'flex-end',
        },
        sortingFn: SortTableCellTextPill.sortingFnCustomSortValue,
        sortDefault: 'down',
      };

      table.columns[COL_IDX_BUTTON] = {
        name: '',
        css: {
          'width': colButtonWidth + 'px',
          'justify-content': 'center',
        },
      };
    }


    for (let key in this.activeSubjects) {
      const subj = this.activeSubjects[key];
      let row: SortTableRow = this.tableRows[key];

      if (!row) {
        row = this.tableRows[key] = {
          cells: [],
          context: key,
        };
      }

      this.upsertRowData(row, subj);
      table.rows.push(row);
    }

    this.searchService.setResultQty(table.rows.length);
    this.table$.next(table);

    if (this.alarmsLoaded) {
      if (this.totalCount === 0 || table.rows.length > 0) {
        this.loading = false;
      }
    }
  }


  /**
   * For inserting data into a brand new table row or updating an existing
   * table row.
   *
   * @param row
   * @param subj
   * @private
   */
  protected upsertRowData(row: SortTableRow, subj: ObjectModel) {
    const alarmEvent: AlarmEvent = this.activeAlarms[row.context];
    const detailsUrl = UrlService.makeAlarmEventDetailsUrl(alarmEvent.UUID);

    const unacked: boolean = !alarmEvent.AckTime;
    const ackColor: SortTableStatusDotColor = unacked ? 'red' : 'yellow';
    const ackLabel: string = unacked ? 'Unacknowledged' : 'Acknowledged';

    // Make the entire row clickable by setting href
    row.href = detailsUrl;

    row.cells[COL_IDX_STATUS] = new SortTableCellTextPill('', ackLabel)
      .setPillColor(ackColor)
      .setPillFill('solid')
      .addSortValue(
        ackLabel + alarmEvent.StartTime,
      );

    if (!row.cells[COL_IDX_KEBAB]) {
      // Max 4 options: View Details, Acknowledge, Clear, Chat
      const kebabCell = new SortTableCellKebabMenu(subj.id, [], subj.name, false, 4);

      kebabCell.addClickCallback(() => {
        this.loadKebabOptions(kebabCell, subj, alarmEvent, detailsUrl);
      });

      row.cells[COL_IDX_KEBAB] = kebabCell;
    }

    if (!row.cells[COL_IDX_ICON]) {
      if (subj.type == ObjectType.TILE) {
        let icon: TileIcon = this.tilesService.sessUserTiles[subj.id]?.Appearance?.Icon;
        row.cells[COL_IDX_ICON] = new SortTableCellIcon(icon);
      } else {
        row.cells[COL_IDX_ICON] = new SortTableCellEmpty();
      }
    }

    const cell = new SortTableCellTextPill(subj.name, subj.id)
      .setPillLetter(<PillLetter>subj.type[0])
      .setShowPillFirst(true);

    // For tiles, add display name and facet context if available
    if (subj.type === ObjectType.TILE) {
      const tile = this.tilesService.sessUserTiles[subj.id];
      cell.displayName = tile?.Appearance?.DisplayName;
      cell.facetValues = tile?.facetValues ?? [];
    }

    // For LoRaWAN gateway alarms, show gateway EUI beneath hub name
    if (subj.type === ObjectType.HUB && alarmEvent.EventInfo?.Category === LORAWAN_GATEWAY_INACTIVE) {
      const eui = alarmEvent.EventInfo?.LoRaWAN?.GatewayEUI ?? alarmEvent.IdempotencyKey?.split(':')?.[1];
      if (eui) {
        cell.displayName = `LoRaWAN Gateway ${eui}`;
      }
    }

    row.cells[COL_IDX_SUBJECT] = cell;

    if (!row.cells[COL_IDX_START_TIME]) {
      row.cells[COL_IDX_START_TIME] = new SortTableCellTextPill(
        UtilsService.makeMonospaceTime(alarmEvent.StartTime),
        '',
        true,
      ).addSortValue(alarmEvent.StartTime);
    }

    if (!row.cells[COL_IDX_BUTTON]) {
      row.cells[COL_IDX_BUTTON] = new SortTableCellButtons([{
        id: alarmEvent.UUID,
        text: 'View Details',
        href: detailsUrl,
        colors: ['indigo'],
      }]);
    }
  }


  private loadKebabOptions(kebabCell: SortTableCellKebabMenu, subj: ObjectModel, alarmEvent: AlarmEvent, detailsUrl: string): void {
    if (kebabCell.isLoading) return;

    kebabCell.isLoading = true;

    this.permissionsService.queryPermissions({
      UserID: this.session.userId,
      ObjectID: subj.id,
      ObjectType: subj.type as ObjectType,
      Permissions: [Permission.CAN_CHAT],
    }).subscribe({
      next: (response) => {
        const options: Option[] = [
          {
            id: 'dt',
            name: 'View Details',
            href: detailsUrl,
          },
        ];

        if (!alarmEvent.AckTime) {
          options.push({
            id: 'ack',
            name: 'Acknowledge Alarm',
            callback: () => this.alarmsService.acknowledgeEvent$(alarmEvent.UUID).subscribe(),
          });
        }

        options.push({
          id: 'cl',
          name: 'Clear Alarm',
          callback: () => this.alarmsService.clearEvent$(alarmEvent.UUID).subscribe(),
        });

        if (response.Results.find(r => r.Permission === Permission.CAN_CHAT)?.Allowed) {
          options.push({
            id: 'chat',
            name: 'Chat',
            icon: 'fa-kit fa-chat',
            callback: () => this.motiChatService.openChat(subj.id, subj.name, subj.type as ObjectType),
          });
        }

        kebabCell.options = options;
        kebabCell.isLoading = false;
      },
    });
  }


  /**
   *
   * @private
   */
  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      '',
      'Current Alarms',
    ));

    this.store.dispatch(new TitleBreadcrumbsChangedAction(
      [
        {
          text: 'Alarms',
        },
        {
          text: 'Current',
        },
      ],
    ));
  }
}
